import { Loader2 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AuthApiError } from "@/lib/auth";

interface ConfirmUserActionDialogProps {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  loadingLabel: string;
  destructive?: boolean;
  errorMessage: (err: unknown) => string;
  onConfirm: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

// Single-step confirmation dialog used by force-logout / reset-2fa /
// send-password-reset. Holds its own submitting + error state so the parent
// just hands off `onConfirm` and reacts to `onSuccess`.
export function ConfirmUserActionDialog({
  open,
  title,
  description,
  confirmLabel,
  loadingLabel,
  destructive,
  errorMessage,
  onConfirm,
  onOpenChange,
  onSuccess,
}: ConfirmUserActionDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-muted-foreground">{description}</div>
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {loadingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Default error formatter — translates the most common admin-action error
// codes into a friendly sentence. Pages can wrap or override per call.
export function defaultActionErrorMessage(err: unknown): string {
  if (err instanceof AuthApiError) {
    switch (err.code) {
      case "not_found":
        return "That user no longer exists. Refresh the list and try again.";
      case "self_modification_blocked":
        return err.serverMessage ?? "You can't perform this action on yourself.";
      case "last_admin":
        return "At least one active admin must remain. Promote another user to admin first.";
      case "account_disabled":
        return "This account is disabled. Re-enable it before sending password reset.";
      case "rate_limited":
        return "Too many attempts in a short window. Please wait a minute and retry.";
      default:
        return err.serverMessage ?? "Something went wrong. Please try again.";
    }
  }
  return "Couldn't reach the server. Check your connection and retry.";
}
